import { Controller } from "@hotwired/stimulus"

// Global keyboard shortcuts for the SIEM search page.
// Mounted on document.body or the main container — no targets needed.
//
// /         → focus the query textarea
// Escape    → blur textarea, close open panels, deselect rows
// ?         → show/hide shortcut cheatsheet
// Ctrl+K    → same as / (common search convention)
// Ctrl+/    → toggle cheatsheet

const SHORTCUTS = [
  { key: "/",        mod: "",      desc: "Focus search bar" },
  { key: "K",        mod: "Ctrl",  desc: "Focus search bar" },
  { key: "Enter",    mod: "Ctrl",  desc: "Run search" },
  { key: "Escape",   mod: "",      desc: "Blur / close panels" },
  { key: "?",        mod: "",      desc: "Show this help" },
  { key: "/",        mod: "Ctrl",  desc: "Toggle shortcut help" },
]

export default class extends Controller {
  #cheatsheet = null
  #boundKey = null

  connect() {
    this.#boundKey = e => this.#onKey(e)
    document.addEventListener("keydown", this.#boundKey)
  }

  disconnect() {
    document.removeEventListener("keydown", this.#boundKey)
    this.#cheatsheet?.remove()
    this.#cheatsheet = null
  }

  #onKey(e) {
    const tag = document.activeElement?.tagName?.toLowerCase()
    const inInput = ["input", "textarea", "select"].includes(tag) ||
                    document.activeElement?.isContentEditable

    // Escape: always handled
    if (e.key === "Escape" && !e.metaKey && !e.altKey) {
      this.#handleEscape()
      return
    }

    // ? or Ctrl+/: toggle cheatsheet — only outside inputs
    if (!inInput && e.key === "?" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      this.#toggleCheatsheet()
      return
    }
    if (e.key === "/" && (e.ctrlKey || e.metaKey) && !e.altKey) {
      e.preventDefault()
      this.#toggleCheatsheet()
      return
    }

    // / or Ctrl+K: focus search — only outside inputs
    if (!inInput) {
      if ((e.key === "/" && !e.ctrlKey && !e.metaKey) ||
          (e.key === "k" && (e.ctrlKey || e.metaKey) && !e.altKey)) {
        e.preventDefault()
        this.#focusSearch()
        return
      }
    }
  }

  #focusSearch() {
    const ta = document.querySelector("textarea[name='query']")
    if (!ta) return
    ta.focus()
    ta.setSelectionRange(ta.value.length, ta.value.length)
  }

  #handleEscape() {
    // Close cheatsheet
    if (this.#cheatsheet) { this.#closeCheatsheet(); return }

    const active = document.activeElement
    const ta = document.querySelector("textarea[name='query']")

    // Blur textarea
    if (active === ta) { ta.blur(); return }

    // Close any open SIEM panels (they all use .hidden to toggle)
    const panels = document.querySelectorAll(
      ".siem-det-panel:not(.hidden), " +
      ".siem-group-by-panel:not(.hidden), .siem-col-mgr-panel:not(.hidden), " +
      ".siem-notebook-panel:not(.hidden), .siem-query-history-panel:not(.hidden)"
    )
    if (panels.length) {
      panels.forEach(p => {
        p.classList.add("hidden")
        // de-activate sibling trigger button
        p.parentElement?.querySelector(".active")?.classList.remove("active")
      })
      return
    }

    // Clear row selections
    const selected = document.querySelectorAll(".siem-row-checkbox:checked")
    if (selected.length) {
      selected.forEach(cb => { cb.checked = false; cb.dispatchEvent(new Event("change", { bubbles: true })) })
      document.querySelector(".siem-select-all")?.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  #toggleCheatsheet() {
    this.#cheatsheet ? this.#closeCheatsheet() : this.#openCheatsheet()
  }

  #openCheatsheet() {
    const overlay = document.createElement("div")
    overlay.className = "siem-kb-overlay"
    overlay.innerHTML = `
      <div class="siem-kb-modal" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts">
        <div class="siem-kb-header">
          <span class="siem-kb-title"><i class="fa fa-keyboard-o"></i> Keyboard Shortcuts</span>
          <button class="siem-kb-close" type="button" aria-label="Close">×</button>
        </div>
        <table class="siem-kb-table">
          <tbody>
            ${SHORTCUTS.map(s => `
              <tr>
                <td class="siem-kb-key">
                  ${s.mod ? `<kbd>${s.mod}</kbd> + ` : ""}<kbd>${this.#esc(s.key)}</kbd>
                </td>
                <td class="siem-kb-desc">${this.#esc(s.desc)}</td>
              </tr>`).join("")}
          </tbody>
        </table>
      </div>`
    overlay.querySelector(".siem-kb-close").addEventListener("click", () => this.#closeCheatsheet())
    overlay.addEventListener("click", e => { if (e.target === overlay) this.#closeCheatsheet() })
    document.body.appendChild(overlay)
    this.#cheatsheet = overlay
  }

  #closeCheatsheet() {
    this.#cheatsheet?.remove()
    this.#cheatsheet = null
  }

  #esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
